import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  BarChart3,
  Lightbulb,
  FileText,
  BookOpen,
} from 'lucide-react';
import { storage } from '../services/storage';
import { API_BASE_URL } from '../utils/api'; // Environment-aware API URL

// Import proper Navigation Component  
import { CmpBootomnavCoach } from '../anima/Navigation Component/src/components/CmpBootomnavCoach/CmpBootomnavCoach';
import '../anima/Navigation Component/src/components/CmpBootomnavCoach/style.css';

// Import StateRead component CSS only (not the layout CSS that was causing positioning issues)
import '../anima/Momentum/Momentum - Active/src/components/StateRead/style.css';

// Create Insights.css for navigation fixes
import './Insights.css';
import './InsightsV2.css';
import './Settings.css'; // re-use bottom-sheet drawer shell for Cedar Reports
import { ChatWithCedarIcon } from './ChatWithCedarIcon';
import { CedarReportDrawerContent } from './CedarReportDrawerContent';
import { CedarInsightsTab } from './CedarInsightsTab';
import { LibraryTab } from './LibraryTab';

// 🔧 FIX (Dec 2025): Correct 3-tab navigation structure
// - Cedar Insights: Cedar Reports (weekly performance analysis)
// - Sources: Research events timeline (Recent/Move/Chat filters)
// - Library: Peer-reviewed studies + Interpreters/Frameworks browser
type InsightsFilter = 'cedar-insights' | 'sources' | 'library';

// Cedar Report Types
interface SpotlightMetric {
  label: string;
  value: string;
  delta?: string;
  delta_direction?: 'positive' | 'negative' | 'neutral';
  context: string;
}

interface DashboardMetric {
  label: string;
  value: string;
  icon: string;
  color: 'success' | 'warning' | 'info' | 'neutral';
  subtitle: string;
}

interface ReportHighlight {
  text: string;
  type: 'win' | 'growth' | 'resilience' | 'milestone';
  related_metric?: string;
}

interface CedarReport {
  id: string;
  week_start_date: string;
  week_summary: string;
  generated_at: string;
  is_read: boolean;
  tone: string;
  insights_direction: string;
  consistency_score: string;
  report_version?: number;
  // v2 stat box fields
  momentum_score?: number;
  momentum_direction?: 'rising' | 'steady' | 'dipping';
  spotlight_metric?: SpotlightMetric;
  dashboard_metrics?: DashboardMetric[];
  highlights?: ReportHighlight[];
  health_context?: {
    sleep_insights: string;
    nutrition_insights: string;
    recovery_patterns: string;
    performance_correlations: string;
  };
}

interface DetailedCedarReport extends CedarReport {
  look_back?: {
    wins: string[];
    challenges: string[];
    insights: string;
  };
  look_forward?: {
    focus_areas: string[];
    recommended_adjustments: string;
    motivation: string;
  };
  goal_alignment?: {
    on_track_goals: string[];
    needs_attention: string[];
    next_week_priorities: string;
  };
  key_metrics?: {
    consistency_score: string;
    insights_direction: string;
    engagement_level: string;
  };
  health_context?: {
    sleep_insights: string;
    nutrition_insights: string;
    recovery_patterns: string;
    performance_correlations: string;
  };
}

// interface ResearchSource { // Unused - defined in utils/researchEngine.ts
//   id: string;
//   title: string;
//   authors: string[];
//   journal: string;
//   publication_date: string;
//   doi?: string;
//   abstract: string;
//   relevance_score: number;
//   topics: string[];
//   plan_context?: {
//     plan_id: string;
//     plan_title: string;
//     guidance_type: 'move' | 'nutrition' | 'sleep';
//   };
//   chat_context?: {
//     question: string;
//     response_id: string;
//     timestamp: string;
//   };
// }

// interface MediaResource { // Unused - defined in utils/researchEngine.ts
//   id: string;
//   title: string;
//   creator: string;
//   platform: 'podcast' | 'youtube' | 'article' | 'blog';
//   url: string;
//   duration?: string;
//   description: string;
//   topics: string[];
//   quality_score: number;
//   plan_context?: {
//     plan_id: string;
//     plan_title: string;
//     guidance_type: 'move' | 'nutrition' | 'sleep';
//   };
//   chat_context?: {
//     question: string;
//     response_id: string;
//     timestamp: string;
//   };
// }



// Simple back button component
const BackButton: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  return (
    <div 
      className="chevron-left-wrapper" 
      onClick={onClick} 
      style={{ 
        cursor: 'pointer',
        position: 'absolute',
        left: '16px',
        top: '16px',
        zIndex: 10,
        backgroundColor: 'var(--colorsurface-100)',
        height: '48px',
        width: '390px'
      }}
    >
      <svg 
        className="chevron-left"
        fill="none"
        height="24"
        viewBox="0 0 24 24"
        width="24"
        xmlns="http://www.w3.org/2000/svg"
        style={{
          position: 'absolute',
          left: '16px',
          top: '12px'
        }}
      >
        <path
          d="M15 18L9 12L15 6"
          stroke="#1E1E1E"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
        />
      </svg>
    </div>
  );
};

export const Insights: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [insightsFilter, setInsightsFilter] = useState<InsightsFilter>('cedar-insights');
  const [reports, setReports] = useState<CedarReport[]>([]);
  const [selectedReport, setSelectedReport] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [mockReportVisible, setMockReportVisible] = useState(false);

  const insightsMainSliderTrackRef = useRef<HTMLDivElement>(null);
  const insightsMainTouchStartXRef = useRef<number | null>(null);

  // Research system now handled by SourcesAndResources component

  const handleNavigation = (tab: string) => {
    switch (tab) {
      case 'cedar':
        navigate('/chat');
        break;
      case 'my-path':
      case 'mypath':
        navigate('/mypath');
        break;
      // HEALTH SECTION TEMPORARILY REMOVED FOR MVP
      // case 'health':
      //   navigate('/health');
      //   break;
      case 'momentum':
      case 'insights':
        // 🏠 HOME BUTTON PATTERN (Feb 3, 2026): Reset to Cedar Insights tab when Insights button clicked
        // Even if already on Insights page, clicking Insights tab should reset to home state
        console.log('🏠 Insights: Insights tab clicked - resetting to Cedar Insights tab');
        
        // Reset to Cedar Insights tab (home state)
        setInsightsFilter('cedar-insights');
        
        // Close any open drawers/modals
        setSelectedReport(null);
        setMockReportVisible(false);
        
        // Scroll to top for fresh view
        window.scrollTo({ top: 0, behavior: 'smooth' });
        break;
      case 'settings':
        navigate('/settings');
        break;
      default:
        break;
    }
  };

  // Fetch user's insights reports
  const fetchReports = async () => {
    if (!user?.id) {
      console.log('No user ID, skipping fetchReports');
      return;
    }

    console.log('Fetching reports for user:', user.id);
    try {
      setLoading(true);
      
      const token = await storage.getItem('jwt_token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      };

      const response = await fetch(`${API_BASE_URL}/api/insights/reports`, { headers });
      
      console.log('Response status:', response.status, response.statusText);
      if (!response.ok) {
        throw new Error(`Failed to fetch insights reports: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Received reports data:', data);
      // Ensure data is always an array
      const reportsArray = Array.isArray(data) ? data : data.reports || [];
      setReports(reportsArray);
      console.log('Set reports to:', reportsArray);
    } catch (err) {
      console.error('Error loading insights:', err);
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  // Research is now handled by the elite research hook

  // Handle report card click
  const handleReportClick = async (reportId: string) => {
    setSelectedReport(reportId);
    
    // Mark as read optimistically for real reports
    setReports(prev => 
      prev.map(r => 
        r.id === reportId ? { ...r, is_read: true } : r
      )
    );
    
    // Persist to backend
    try {
      const token = await storage.getItem('jwt_token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      };

      await fetch(`${API_BASE_URL}/api/insights/reports/${reportId}/read`, {
        method: 'POST',
        headers,
      });
    } catch (error) {
      console.error('Failed to mark report as read:', error);
      // Revert optimistic update on error
      setReports(prev => 
        prev.map(r => 
          r.id === reportId ? { ...r, is_read: false } : r
        )
      );
    }
  };

  // Handle source/resource clicks
  // Source and resource clicks now handled by SourcesAndResources component

  // Close detail view (mark-as-read already handled in handleReportClick)
  const handleCloseDetail = () => {
    setSelectedReport(null);
  };

  useEffect(() => {
    if (insightsFilter === 'cedar-insights') {
      fetchReports();
    }
    // Research is automatically handled by the research hook
  }, [user?.id, insightsFilter]);

  // Handle navigation from chat with source selection
  useEffect(() => {
    if (location.state?.from === 'chat') {
      if (location.state.filter) {
        setInsightsFilter(location.state.filter);
      }
    }
  }, [location.state]);

  // Handle deep-links from URL params (?tab=cedar-insights&report={id})
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab === 'sources' || tab === 'cedar-insights' || tab === 'library') {
      setInsightsFilter(tab as InsightsFilter);
    } else if (tab) {
      // Legacy: any other tab param defaults to sources
      setInsightsFilter('sources');
    }
    // Auto-open a specific report if report param is present
    const reportId = params.get('report');
    if (reportId) {
      setSelectedReport(reportId);
    }
  }, [location.search]);

  // Get all reports including default first report
  const getAllReports = () => {
    // Ensure reports is always an array before spreading
    const safeReports = Array.isArray(reports) ? reports : [];
    return safeReports;
  };

  // Get selected report data
  const getSelectedReportData = (): DetailedCedarReport | null => {
    const report = reports.find(r => r.id === selectedReport);
    if (!report) return null;
    
    // For now, return the basic report as detailed (until backend provides full data)
    return report as DetailedCedarReport;
  };

  // Filtered sources and resources are now handled by the research hook
  // Sources and resources now handled by SourcesAndResources component

  // Reports are now rendered as a bottom-sheet drawer (same shell as Settings).

  // Main Insights view with filter tabs
  return (
    <>
    <div className="clean-chat-container insights-container insights-page flex flex-col h-full bg-white max-w-md mx-auto">
      <div className="flex flex-col h-full bg-white">
        {/* Header */}
        <div className="p-4 pb-2">
          <h1 className="insights-title">Insights</h1>
        </div>

        {/* Main Filter Slider - Insights | Sources | Library with sliding pill and swipe */}
        <div className="px-4 pb-2">
          <div
            ref={insightsMainSliderTrackRef}
            className="insights-main-slider-track"
            role="tablist"
            aria-label="Insights sections"
            onTouchStart={(e) => {
              insightsMainTouchStartXRef.current = e.targetTouches[0].clientX;
            }}
            onTouchEnd={(e) => {
              const startX = insightsMainTouchStartXRef.current;
              if (startX == null) return;
              const endX = e.changedTouches[0].clientX;
              const deltaX = endX - startX;
              const threshold = 40;
              const order: InsightsFilter[] = ['cedar-insights', 'sources', 'library'];
              const idx = order.indexOf(insightsFilter);
              if (deltaX < -threshold && idx < 2) setInsightsFilter(order[idx + 1]);
              else if (deltaX > threshold && idx > 0) setInsightsFilter(order[idx - 1]);
              insightsMainTouchStartXRef.current = null;
            }}
            onMouseDown={(e) => {
              insightsMainTouchStartXRef.current = e.clientX;
            }}
            onMouseUp={(e) => {
              const startX = insightsMainTouchStartXRef.current;
              if (startX == null) return;
              const deltaX = e.clientX - startX;
              const threshold = 40;
              const order: InsightsFilter[] = ['cedar-insights', 'sources', 'library'];
              const idx = order.indexOf(insightsFilter);
              if (deltaX < -threshold && idx < 2) setInsightsFilter(order[idx + 1]);
              else if (deltaX > threshold && idx > 0) setInsightsFilter(order[idx - 1]);
              insightsMainTouchStartXRef.current = null;
            }}
            onMouseLeave={() => {
              insightsMainTouchStartXRef.current = null;
            }}
          >
            <div
              className="insights-main-slider-pill"
              style={{
                transform:
                  insightsFilter === 'cedar-insights'
                    ? 'translateX(0)'
                    : insightsFilter === 'sources'
                    ? 'translateX(100%)'
                    : 'translateX(200%)',
              }}
              aria-hidden
            />
            <button
              type="button"
              role="tab"
              aria-selected={insightsFilter === 'cedar-insights'}
              className={`insights-main-slider-segment ${insightsFilter === 'cedar-insights' ? 'insights-main-slider-active' : ''}`}
              onClick={() => setInsightsFilter('cedar-insights')}
            >
              <BarChart3 size={16} />
              Insights
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={insightsFilter === 'sources'}
              className={`insights-main-slider-segment ${insightsFilter === 'sources' ? 'insights-main-slider-active' : ''}`}
              onClick={() => setInsightsFilter('sources')}
            >
              <FileText size={16} />
              Sources
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={insightsFilter === 'library'}
              className={`insights-main-slider-segment ${insightsFilter === 'library' ? 'insights-main-slider-active' : ''}`}
              onClick={() => setInsightsFilter('library')}
            >
              <BookOpen size={16} />
              Library
            </button>
          </div>
        </div>

        {/* Sources Notice */}
        {insightsFilter === 'sources' && (
          <div className="px-4 pb-3">
            <div className="insights-notice">
              <FileText size={16} style={{ color: '#1f6c4f', marginTop: '2px' }} />
              <div>
                <strong>Research-backed guidance.</strong> See exactly which sources Cedar used for your coaching.
              </div>
            </div>
          </div>
        )}

        {/* Library Notice */}
        {insightsFilter === 'library' && (
          <div className="px-4 pb-3">
            <div className="insights-notice">
              <BookOpen size={16} style={{ color: '#1f6c4f', marginTop: '2px' }} />
              <div>
                <strong>Research Library.</strong> Explore peer-reviewed sources, expert resources, and practical frameworks.
              </div>
            </div>
          </div>
        )}

        {/* Content Area */}
        <div className={`flex-1 overflow-y-auto pb-20 ${insightsFilter !== 'cedar-insights' ? 'px-0' : 'px-4'}`}>
          {insightsFilter === 'cedar-insights' && (
            // Cedar Insights Content - Cedar Reports
            <div className="cardslive-insights">
              <div className="div-2">
                <div className="vertical-list-frame">
                  {loading ? (
                    <div style={{ 
                      padding: '40px 20px', 
                      textAlign: 'center',
                      color: '#6b7280'
                    }}>
                      <div style={{
                        width: '24px',
                        height: '24px',
                        border: '2px solid #e5e7eb',
                        borderTop: '2px solid #6366f1',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        margin: '0 auto 16px'
                      }}></div>
                      <p>Loading your Cedar Reports...</p>
                    </div>
                  ) : reports.length === 0 ? (
                    <div className="space-y-3">
                      <div className="insights-card">
                        <div className="insights-card-title">
                          <BarChart3 size={20} style={{ color: '#1f6c4f' }} />
                          Cedar Reports
                        </div>
                        <div className="insights-card-body">
                          Your weekly performance analysis and coaching insights will appear here once you’ve completed your first week.
                        </div>
                        <button type="button" className="insights-primary-btn" onClick={() => setMockReportVisible(true)}>
                          <BarChart3 size={16} />
                          Preview sample report
                        </button>
                      </div>

                      <div className="insights-card">
                        <div className="insights-card-title">
                          <Lightbulb size={20} style={{ color: '#1f6c4f' }} />
                          Research transparency
                        </div>
                        <div className="insights-card-body">
                          Every insight is backed by evidence. See which sources Cedar used in <strong>Sources</strong>, or browse the full research library.
                        </div>
                        <button
                          type="button"
                          className="insights-primary-btn"
                          onClick={() => setInsightsFilter('sources')}
                        >
                          View research sources
                        </button>
                      </div>
                    </div>
                  ) : (
                    getAllReports().map((report) => (
                        <div
                          key={report.id}
                          className="state-read cmp-debriefcard"
                          onClick={() => handleReportClick(report.id)}
                          style={{ cursor: 'pointer', position: 'relative' }}
                        >
                          {!report.is_read && (
                            <div style={{
                              position: 'absolute',
                              top: '11px',
                              left: '13px',
                              width: '10px',
                              height: '10px',
                              backgroundColor: '#e53e3e',
                              borderRadius: '50%',
                              border: '2px solid white',
                              boxShadow: '0 2px 4px rgba(229, 62, 62, 0.3)',
                              zIndex: 10
                            }}></div>
                          )}
                          <p className="p">
                            {report.id === "first-week-insights"
                              ? "Cedar Report"
                              : new Date(report.week_start_date).toLocaleDateString('en-US', {
                                  month: 'long',
                                  day: 'numeric',
                                  year: 'numeric'
                                })
                            }
                          </p>
                          <div className="text-wrapper-4">
                            {report.id === "first-week-insights"
                              ? "First Week Insights"
                              : report.week_summary
                                ? (report.week_summary.length > 80 ? report.week_summary.slice(0, 80) + '…' : report.week_summary)
                                : "Weekly Analysis"
                            }
                          </div>
                          <div className="frame-2" />
                        </div>
                      ))
                  )}
                </div>
              </div>
            </div>
          )}

          {insightsFilter === 'sources' && (
            // Sources Tab - Research events timeline (Recent/Move/Chat)
            user?.id ? (
              <CedarInsightsTab userId={user.id} />
            ) : (
              <div className="px-4">
                <div className="insights-card">
                  <div className="insights-card-title">
                    <FileText size={20} style={{ color: '#1f6c4f' }} />
                    Loading
                  </div>
                  <div className="insights-card-body">
                    Setting up your Sources… please wait a moment.
                  </div>
                </div>
              </div>
            )
          )}

          {insightsFilter === 'library' && (
            // Library Tab - Browse peer-reviewed + interpreters/frameworks
            user?.id ? (
              <LibraryTab userId={user.id} />
            ) : (
              <div className="px-4">
                <div className="insights-card">
                  <div className="insights-card-title">
                    <BookOpen size={20} style={{ color: '#1f6c4f' }} />
                    Loading
                  </div>
                  <div className="insights-card-body">
                    Setting up your Library… please wait a moment.
                  </div>
                </div>
              </div>
            )
          )}
        </div>
        
        {/* Navigation */}
        <div className="navigation-container">
          <CmpBootomnavCoach 
            activeTab="insights"
            className=""
            onClickTab={handleNavigation as any}
          />
        </div>
      </div>

      {/* Mock Report Modal */}
      {/* Report Drawer (template for all reports) */}
      {(mockReportVisible || !!selectedReport) && (
        <div className="drawer-overlay" onClick={() => { setMockReportVisible(false); handleCloseDetail(); }}>
          <div className="drawer-content" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-handle" />
            <button
              type="button"
              className="drawer-close"
              onClick={() => { setMockReportVisible(false); handleCloseDetail(); }}
              aria-label="Close"
            >
              ×
            </button>
            <div className="drawer-scroll">
              <CedarReportDrawerContent
                mode={mockReportVisible ? 'mock' : 'report'}
                report={selectedReport ? getSelectedReportData() : null}
              />
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};
